/**
 * Renders a risk-assessment report.
 *
 * The report is **Markdown** (prompt `veeva-release-risk/2`), and the shape that
 * matters is the table: a release comparison is area / before / after / risk,
 * which is a grid, and prose forced the reader to rebuild that grid mentally.
 * So tables render as real tables.
 *
 * ## Why a hand-rolled renderer rather than a Markdown library
 *
 * Only the constructs the prompt is *instructed* to emit are handled — `##` and
 * `###` headings, `- ` bullets, `|` tables, paragraphs, `**bold**` and
 * `` `code` `` inline. Anything else renders as plain text rather than being
 * dropped.
 *
 * That is deliberate. A general Markdown renderer would accept raw HTML and
 * links from **model output**, which is untrusted text that quotes a customer's
 * documents — an injection surface for a feature whose entire value is
 * trustworthiness. Restricting the grammar to what we asked for means there is
 * no path from model output to markup.
 *
 * ## Fail open, never blank
 *
 * If the model drifts from the format, every unrecognised line still renders as
 * a paragraph. A report that looks plain is a cosmetic problem; a report that
 * renders empty because a parser bailed is a safety problem — the reader would
 * conclude there were no findings.
 */

import { Fragment } from "react";
import { Badge, Code, Divider, Stack, Table, Text, Title } from "@mantine/core";

/** Colour per GxP risk word, matching the shared status ramp: red = act,
 *  yellow = watch, grey = informational. Keyed loosely because this comes from
 *  model output, so an unexpected word must degrade rather than throw. */
const RISK_COLOR: Record<string, string> = {
  high: "red",
  medium: "yellow",
  low: "gray",
  "n/a": "gray",
  none: "gray",
};

/** Words that, appearing alone in a cell, are worth a badge rather than text —
 *  they are the values a reader scans for. */
const BADGE_WORDS = new Set([
  "high",
  "medium",
  "low",
  "n/a",
  "auto-on",
  "admin checkbox",
  "configuration",
  "support",
  "migration",
]);

type Block =
  | { kind: "heading"; level: number; text: string }
  | { kind: "para"; text: string }
  | { kind: "bullet"; text: string }
  | { kind: "table"; header: string[]; rows: string[][] }
  | { kind: "rule" };

function splitRow(line: string): string[] {
  return line
    .trim()
    .replace(/^\||\|$/g, "")
    .split("|")
    .map((c) => c.trim());
}

/** A Markdown table's `|---|:--:|` separator row. */
function isSeparator(line: string): boolean {
  const body = line.trim().replace(/^\||\|$/g, "");
  if (!body.trim()) return false;
  return body.split("|").every((c) => /^:?-{1,}:?$/.test(c.trim()));
}

export function parseMarkdown(md: string): Block[] {
  const lines = md.replace(/\r\n/g, "\n").split("\n");
  const blocks: Block[] = [];
  let paragraph: string[] = [];

  const flush = () => {
    if (paragraph.length > 0) {
      blocks.push({ kind: "para", text: paragraph.join(" ") });
      paragraph = [];
    }
  };

  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i] ?? "";
    const t = line.trim();

    if (!t) {
      flush();
      continue;
    }

    // A table is a pipe row followed by a separator row. Checking the *next*
    // line is what stops a sentence containing a pipe becoming a table.
    const next = lines[i + 1];
    if (t.startsWith("|") && next !== undefined && isSeparator(next)) {
      flush();
      const header = splitRow(t);
      const rows: string[][] = [];
      i += 2;
      while (i < lines.length && (lines[i] ?? "").trim().startsWith("|")) {
        rows.push(splitRow(lines[i] ?? ""));
        i += 1;
      }
      i -= 1;
      blocks.push({ kind: "table", header, rows });
      continue;
    }

    if (/^(-{3,}|\*{3,}|_{3,})$/.test(t)) {
      flush();
      blocks.push({ kind: "rule" });
      continue;
    }

    const heading = /^(#{1,4})\s+(.*)$/.exec(t);
    if (heading) {
      flush();
      blocks.push({
        kind: "heading",
        level: (heading[1] ?? "#").length,
        text: heading[2] ?? "",
      });
      continue;
    }

    if (/^[-*]\s+/.test(t)) {
      flush();
      blocks.push({ kind: "bullet", text: t.replace(/^[-*]\s+/, "") });
      continue;
    }

    paragraph.push(t);
  }
  flush();
  return blocks;
}

/** Render `**bold**` and `` `code` `` without allowing any other markup. */
function Inline({ text }: { text: string }) {
  const parts = text.split(/(\*\*[^*]+\*\*|`[^`]+`)/g).filter((p) => p !== "");
  return (
    <>
      {parts.map((part, i) => {
        const key = `${i}:${part}`;
        if (part.startsWith("**") && part.endsWith("**")) {
          return (
            <Text span fw={700} key={key}>
              {part.slice(2, -2)}
            </Text>
          );
        }
        if (part.startsWith("`") && part.endsWith("`") && part.length > 2) {
          return (
            <Code key={key} fz="xs">
              {part.slice(1, -1)}
            </Code>
          );
        }
        return <Fragment key={key}>{part}</Fragment>;
      })}
    </>
  );
}

/** A cell whose whole content is a risk or enablement word becomes a badge —
 *  those are the values a reader scans a comparison table for. */
function Cell({ text }: { text: string }) {
  const bare = text.replace(/\*\*/g, "").trim();
  const lower = bare.toLowerCase();
  if (BADGE_WORDS.has(lower)) {
    return (
      <Badge size="sm" variant="light" color={RISK_COLOR[lower] ?? "blue"}>
        {bare}
      </Badge>
    );
  }
  return <Inline text={text} />;
}

export function ReportView({ text }: { text: string }) {
  const blocks = parseMarkdown(text);

  // Nothing parsed at all — show the raw text rather than an empty panel. A
  // blank report reads as "no findings", which would be a lie.
  if (blocks.length === 0) {
    return (
      <Text size="sm" style={{ whiteSpace: "pre-wrap" }}>
        {text}
      </Text>
    );
  }

  return (
    <Stack gap="sm">
      {blocks.map((block, i) => {
        const key = `${block.kind}:${i}`;
        switch (block.kind) {
          case "heading":
            return (
              <Title
                key={key}
                order={block.level <= 2 ? 4 : 5}
                mt={block.level <= 2 ? "sm" : 4}
              >
                <Inline text={block.text} />
              </Title>
            );
          case "rule":
            return <Divider key={key} my="xs" />;
          case "bullet":
            return (
              <Text key={key} size="sm" pl="md" style={{ textIndent: "-0.85em" }}>
                <Text span c="dimmed">
                  •{" "}
                </Text>
                <Inline text={block.text} />
              </Text>
            );
          case "table":
            return (
              // Wide comparison tables must scroll inside their own box; the
              // page itself must never scroll sideways.
              <Table.ScrollContainer key={key} minWidth={520}>
                <Table
                  striped
                  withTableBorder
                  withColumnBorders
                  verticalSpacing={6}
                  fz="xs"
                >
                  <Table.Thead>
                    <Table.Tr>
                      {block.header.map((h, c) => (
                        <Table.Th key={`h${c}:${h}`}>
                          <Inline text={h} />
                        </Table.Th>
                      ))}
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {block.rows.map((row, r) => (
                      <Table.Tr key={`r${r}`}>
                        {/* Pad short rows so a ragged table from the model does
                            not shift columns out of alignment. */}
                        {block.header.map((_, c) => (
                          <Table.Td key={`c${c}`}>
                            <Cell text={row[c] ?? ""} />
                          </Table.Td>
                        ))}
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            );
          default:
            return (
              <Text key={key} size="sm">
                <Inline text={block.text} />
              </Text>
            );
        }
      })}
    </Stack>
  );
}
